import { useMemo, useCallback } from "react"
import { filter } from "lodash"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createContentSetExposureMultiDateQuery, createContentSetExposureIntradayBatchTrendQueries } from "gatherers/multi-date/content-set-exposure"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { filterItemsWithData, extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { numberFormatter } from "helpers/formatters/numbers"
import { ERROR, LOADING, OK } from "helpers/data-statuses"
import { ModelMetadata } from "resources/models"
import { ChartColors } from "connected-components/graph/types"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState, useIntradayBatchTrendMode, getIntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import type { SourceMetricIdsChangeHandler } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import { ContentSetMetadata } from "./types"
import { useContentSetsMetadata } from "resources/content-sets"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_CONTENT_SET_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useActiveContentSet } from "./use-active-content-set"
import { useGraphData } from "../../hooks/use-graph-data"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { useSourceGraphMetricIds, getEnabledItemIds } from "../../hooks/use-source-graph-metric-ids"

interface ContentSetGraphProps {
  modelMetadata: ModelMetadata
  onMetricsChange?: SourceMetricIdsChangeHandler
}

interface ContentSetItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: string
}

function createContentSetItems(
  contentSetMetadata: ContentSetMetadata | null,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
): ContentSetItem[] {
  if (!contentSetMetadata?.factors) return []

  const factors = selectedCategory === "total"
    ? contentSetMetadata.factors
    : filter(contentSetMetadata.factors, factor => factor.category === selectedCategory)

  return factors.map(factor => ({
    id: factor.id,
    label: factor.name,
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor",
  }))
}

export default function ContentSetGraph({ modelMetadata, onMetricsChange }: ContentSetGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(GRAPH_CONTENT_SET_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  const [contentSetsMetadata, contentSetsStatus] = useContentSetsMetadata() as [ContentSetMetadata[] | null, string]

  const { selectedContentSetId, activeContentSet } = useActiveContentSet()
  const [{ lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined) => {
    if (!panelId || !selectedContentSetId || !focusedItemId) return
    // A batch-trend point is stamped with its own batch's asOf, not the latest — resolve the
    // clicked point back to its batch so drilldown doesn't open with the wrong snapshot.
    const clickedBatch = isBatchTrendMode ? completedBatches.find(batch => batch.id === clickPosition?.batchId) : undefined
    // clickedBatch.date is authoritative; clickPosition.date can shift a day across timezones.
    const snapshotDate = clickedBatch?.date ?? clickPosition?.date ?? endDate
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.content-set",
      title: "Asset View: Content Set",
      view: selectedContentSetId,
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        // A live-tick click has no clickedBatch; don't fall back to intradayBatchMetadata's latest batch.
        ...resolveIntradayDrilldownFields(isIntradayMode, isBatchTrendMode ? clickedBatch : intradayBatchMetadata, intradayPricingAsOf),
        filter: portfolioFilter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor: focusedItemId,
        selectedCategory,
        clickedDate: snapshotDate,
      },
    }))
  }, [panelId, openDrilldown, selectedContentSetId, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, portfolioFilter, normalization, isEndVsStartDateComparison, isBatchTrendMode, completedBatches])

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter, normalization)
  const segment = useFilteredSegment(portfolioFilter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(
    () => createContentSetItems(activeContentSet, selectedCategory, disabledItems),
    [activeContentSet, selectedCategory, disabledItems],
  )

  const enabledFactorIds = useMemo(
    () => getEnabledItemIds(currentItems),
    [currentItems],
  )
  useSourceGraphMetricIds({ itemIds: enabledFactorIds, onMetricsChange })

  const filterItems = useCallback(
    (items: ContentSetItem[], data: unknown[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => {
    if (!selectedContentSetId) return []
    const liveQuery = createContentSetExposureMultiDateQuery(
      modelMetadata.id,
      selectedContentSetId,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      isIntradayMode,
      { batchId: intradayBatchId, lastIntradayBatchId },
    )
    // Batches append to the live-tick series instead of replacing it.
    if (!isBatchTrendMode) return [liveQuery]
    return [liveQuery, ...createContentSetExposureIntradayBatchTrendQueries(modelMetadata.id, selectedContentSetId, instrumentWithSegment, baseWithSegment, completedBatches)]
  }, [isBatchTrendMode, completedBatches, modelMetadata, selectedContentSetId, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  if (contentSetsStatus === LOADING) {
    return <MultiLineGraph
      status={LOADING}
      startDate={startDate}
      endDate={endDate}
      items={[]}
      loadingMessage="Loading content sets..."
    />
  }

  if (contentSetsStatus === ERROR) {
    return <MultiLineGraph
      status={ERROR}
      startDate={startDate}
      endDate={endDate}
      items={[]}
      errorMessage="Failed to load content sets. Please try again."
    />
  }

  if (!contentSetsMetadata?.length) {
    return <MultiLineGraph
      status={OK}
      data={[]}
      startDate={startDate}
      endDate={endDate}
      items={[]}
      loadingMessage=""
      emptyMessage="No content sets are configured for this account."
    />
  }

  if (!selectedContentSetId) {
    return <MultiLineGraph
      status={OK}
      data={[]}
      startDate={startDate}
      endDate={endDate}
      items={[]}
      loadingMessage=""
      emptyMessage="Select a content set to view data."
    />
  }

  const intradayChartMode = getIntradayChartMode(isBatchTrendMode, isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId))
  const isOneDayMode = intradayChartMode !== "none"
  const noDataMessage = isOneDayMode
    ? "Intraday content set data isn't available yet."
    : undefined

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractDatesFromEachSeries : extractDatesFromFirstSeries}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      onDotClick: handleDotClick,
      formatter: numberFormatter,
      loadingMessage: "Loading content set data...",
      errorMessage: "Failed to load content set data. Please try again.",
      emptyMessage: "No content set data available for the selected period.",
      noDataMessage,
      isOneDayMode,
      intradayChartMode,
      isTruncated,
    }}
  />
}
